<script setup>
import {
  PopoverRoot,
  PopoverTrigger,
  PopoverContent,
  PopoverPortal,
} from "radix-vue";
import { computed } from "vue";

const props = defineProps({
  lorder: {
    type: Array,
    required: true,
  },
  product: {
    type: Object,
    required: true,
  },
});

const checkPricesSame = () => {
  const prices = props.product.variants.map((v) => v.price);
  return prices.every((p) => p === prices[0]);
};

const getBulkData = () => {
  let data = props.product.bulk_discount || [];
  if (data.length && checkPricesSame()) {
    data = [];
    const firstVariant = props.product.variants[0];

    for (const bd of props.product.bulk_discount) {
      let discountedPrice =
        firstVariant.wholesale_price -
        (firstVariant.wholesale_price / 100) * Number(bd.discount_percentage);

      discountedPrice = Number(discountedPrice.toFixed(2));

      if (discountedPrice >= Number(firstVariant.price)) {
        discountedPrice = Number(firstVariant.price);
      }

      if (
        firstVariant.min_price &&
        discountedPrice < Number(firstVariant.min_price)
      ) {
        discountedPrice = Number(firstVariant.min_price);
      }
      const discountPercentage =
        ((Number(firstVariant.price) - discountedPrice) /
          Number(firstVariant.price)) *
        100;
      const discountPercentageRounded = Number(discountPercentage.toFixed(2));

      const last = data[data.length - 1];
      if (
        discountPercentage > 0 &&
        (!last || last.discount_percentage !== discountPercentageRounded)
      ) {
        data.push({
          min_quantity: bd.min_quantity,
          discount_percentage: discountPercentageRounded,
          discounted_price: discountedPrice,
        });
      }
    }
  }
  return data;
};

const bulkData = computed(() => getBulkData());
</script>

<template>
  <PopoverRoot>
    <PopoverTrigger class="item-fav item-bulk mr-2.5">
      <Icon
        v-tippy="{
          content: 'Click here view bulk pricing',
          placement: 'top',
        }"
        name="package-outline"
      />
    </PopoverTrigger>

    <PopoverPortal>
      <PopoverContent
        class="lorder-panel"
        :side-offset="5"
        aria-label="Bulk Pricing"
      >
        <div
          class="flex flex-col bg-white rounded-lg shadow-box overflow-hidden"
        >
          <div
            class="flex items-center justify-between px-4 py-3 border-b border-theme-bg-shade border-opacity-30 w-full"
          >
            <p
              class="flex items-center text-sm text-theme-secondary font-medium"
            >
              <Icon name="package" class="pkg-icon mr-1.5" />
              Bulk Pricing
            </p>
          </div>
          <div>
            <div class="bulk-pricing-modal inline-info no-shadow">
              <div class="bulk-pricing-header">
                <p>Automatic volume discounts</p>
              </div>
              <div class="bulk-pricing-content">
                <table class="min-w-full divide-y divide-gray-200">
                  <thead class="bg-white">
                    <tr>
                      <th scope="col">QUANTITY</th>
                      <th scope="col">DISCOUNT</th>
                      <th scope="col" v-if="checkPricesSame()">PRICE</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="bd in bulkData" :key="bd.id">
                      <td class="font-medium">{{ bd.min_quantity }}+</td>
                      <td>{{ Number(bd.discount_percentage).toFixed(2) }}%</td>
                      <td v-if="checkPricesSame()">${{ Number(bd.discounted_price).toFixed(2) }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </PopoverContent>
    </PopoverPortal>
  </PopoverRoot>
</template>

<style lang="scss">
.lorder-panel {
  @apply w-auto sm:w-96 sm:rounded-lg rounded-lg shadow-box overflow-hidden bg-white ring-1 ring-black ring-opacity-5 focus:outline-none z-50 h-auto;

  &[data-state="open"] {
    animation: fadeIn 100ms ease-out;
  }
  &[data-state="closed"] {
    animation: fadeOut 75ms ease-in;
  }
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: scale(0.95);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

@keyframes fadeOut {
  from {
    opacity: 1;
    transform: scale(1);
  }
  to {
    opacity: 0;
    transform: scale(0.95);
  }
}
</style>
